import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { Button, Modal, Form, Badge } from 'react-bootstrap';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

//...
const TaskDashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total_points: 0, rank: '-', badge: '-' });
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  const [evidenceText, setEvidenceText] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);

  const fetchTasksAndStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const [taskRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/tasks/stats/me`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTasks(taskRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch tasks/stats', err);
    }
  };

  useEffect(() => {
    fetchTasksAndStats();
  }, []);

  const openClaimModal = (task) => {
    setSelectedTask(task);
    setEvidenceText(task.evidence_text || '');
    setEvidenceFile(null);
    setShowClaimModal(true);
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!evidenceText && !evidenceFile && !selectedTask.evidence_file) {
        return Swal.fire('Error', 'Harap isi text/link bukti atau letakkan file bukti.', 'error');
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('evidence_text', evidenceText);
      if (evidenceFile) {
          formData.append('evidence_file', evidenceFile);
      }

      await axios.put(`${API_URL}/api/tasks/${selectedTask.id}/claim`, formData, {
        headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
      });
      
      Swal.fire('Berhasil', 'Task berhasil diklaim/direvisi', 'success');
      setShowClaimModal(false);
      fetchTasksAndStats();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Gagal mengklaim task', 'error');
    }
  };

  const UPLOADS_BASE = (API_URL || "").replace(/\/api\/?$/, "") || API_URL;

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const columns = [
    { name: 'Title', selector: row => row.title, sortable: true },
    { name: 'Category', selector: row => row.category, sortable: true },
    { name: 'Points', selector: row => row.points, sortable: true },
    { 
      name: 'Deadline', 
      selector: row => new Date(row.deadline).toLocaleDateString('id-ID'), 
      sortable: true,
      cell: row => {
         if (row.status !== 'pending' && row.status !== 'rejected') {
            return new Date(row.deadline).toLocaleDateString('id-ID'); // Udah aman
         }
         const today = new Date();
         today.setHours(0,0,0,0);
         const ddl = new Date(row.deadline);
         
         const diffTime = ddl.getTime() - today.getTime();
         const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
         
         let color = 'inherit';
         let fw = 'normal';
         if (diffDays < 0) { color = 'red'; fw = 'bold'; }
         else if (diffDays <= 2) { color = 'orange'; fw = 'bold'; }
         
         return <span style={{ color, fontWeight: fw }}>{new Date(row.deadline).toLocaleDateString('id-ID')}</span>;
      }
    },
    {
      name: 'Status',
      cell: row => {
        let variant = 'secondary';
        if (row.status === 'claimed') variant = 'warning';
        if (row.status === 'approved') variant = 'success';
        if (row.status === 'rejected') variant = 'danger';
        return <Badge bg={variant}>{row.status.toUpperCase()}</Badge>;
      }
    },
    {
      name: 'Poin Didapat',
      cell: row => {
        if (row.status !== 'approved') return '-';
        const earned = row.earned_points ?? 0;
        const isLate = earned < row.points;
        return (
          <div style={{ fontSize: '12px' }}>
            <strong>{earned} poin</strong>
            {isLate && (
              <div className="text-muted">({row.points} poin, 50% karena telat)</div>
            )}
          </div>
        );
      }
    },
    {
      name: 'Tanggal Selesai',
      selector: row => row.updated_at,
      sortable: true,
      cell: row => (row.status === 'approved' ? formatDate(row.updated_at) : '-')
    },
    {
      name: 'Evidence',
      cell: row => {
          if (row.status === 'pending') return '-';
          return (
              <div style={{fontSize: '12px'}}>
                  {row.evidence_text && <div><b>Teks/Link:</b> {row.evidence_text}</div>}
                  {row.evidence_file && <div><a href={`${UPLOADS_BASE}/uploads/${row.evidence_file}`} target="_blank" rel="noopener noreferrer">View File</a></div>}
              </div>
          );
      }
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex flex-col gap-1 p-1">
          {row.status === 'pending' && (
            <Button variant="primary" size="sm" onClick={() => openClaimModal(row)}>Klaim Selesai</Button>
          )}
          {row.status === 'rejected' && (
            <Button variant="danger" size="sm" onClick={() => openClaimModal(row)}>Revisi Selesai</Button>
          )}
        </div>
      )
    }
  ];

  const ExpandedComponent = ({ data }) => (
    <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>
      <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <strong>Title Lengkap:</strong><br/>
        {data.title}
      </p>
      {data.status === 'rejected' && data.admin_feedback && (
        <div className="mt-2 text-danger">
          <strong>Alasan Penolakan/Revisi:</strong><br/>
          <span style={{ whiteSpace: 'pre-wrap' }}>{data.admin_feedback}</span>
        </div>
      )}
      {data.status === 'approved' && (
        <div className="mt-2 text-success">
          <strong>Poin Didapat:</strong> {data.earned_points ?? 0} poin
          {data.earned_points < data.points && (
            <span className="text-muted"> (dari {data.points} poin, dipotong 50% karena melewati deadline)</span>
          )}
          <br />
          <strong>Tanggal Selesai:</strong> {formatDate(data.updated_at)}
          {data.claimed_at && (
            <>
              <br />
              <strong>Tanggal Klaim:</strong> {formatDate(data.claimed_at)}
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout role="officer">
      <div className="p-4">
        
        {/* GAMIFICATION DASHBOARD */}
        <div className="row mb-4">
            <div className="col-md-4">
                <div className="card text-center shadow-sm">
                    <div className="card-body">
                        <p className="text-muted mb-1">Pencapaian Poin Kamu</p>
                        <h3 className="text-primary fw-bold mb-0">{stats.total_points} Poin</h3>
                    </div>
                </div>
            </div>
            <div className="col-md-4">
                <div className="card text-center shadow-sm">
                    <div className="card-body">
                        <p className="text-muted mb-1">Ranking Officer</p>
                        <h3 className="text-warning fw-bold mb-0"># {stats.rank}</h3>
                    </div>
                </div>
            </div>
            <div className="col-md-4">
                <div className="card text-center shadow-sm border-primary">
                    <div className="card-body">
                        <p className="text-muted mb-1">Badge Spesial</p>
                        <h3 className="text-primary fw-bold mb-0">🏅 {stats.badge}</h3>
                    </div>
                </div>
            </div>
        </div>

        <h2 className="mb-4 text-2xl font-bold">My Tasks</h2>
        
        <DataTable
          columns={columns}
          data={tasks}
          pagination
          highlightOnHover
          responsive
          expandableRows
          expandOnRowClicked
          expandableRowsComponent={ExpandedComponent}
        />

        <Modal show={showClaimModal} onHide={() => setShowClaimModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{selectedTask?.status === 'rejected' ? 'Revisi Task Selesai' : 'Klaim Task Selesai'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleClaimSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Teks Bukti / Link (Opsional bila ada lampiran file)</Form.Label>
                <Form.Control as="textarea" rows={3} value={evidenceText} onChange={(e) => setEvidenceText(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>File Bukti Lampiran (Opsional)</Form.Label>
                <Form.Control type="file" onChange={(e) => setEvidenceFile(e.target.files[0])} />
                {selectedTask?.evidence_file && <small className="text-muted mt-1 d-block">Sudah ada file sebelumnya.</small>}
              </Form.Group>
              <div className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => setShowClaimModal(false)}>Cancel</Button>
                <Button variant="primary" type="submit">{selectedTask?.status === 'rejected' ? 'Update Klaim' : 'Klaim'}</Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default TaskDashboard;
